/**
 * Configuración de la vista de lista "Productos" (metadata-driven).
 */

import { ProductIcon, PlusIcon } from '@/lib/polaris-icons';
import type { ListViewConfig } from '@/types/listView.types';
import { getStatusBadgeTone } from '@/types/listView.types';
import { ROUTES } from '@/config/routes.config';
import { makeChannelsExpandable, type ProductRowChannelFields } from './productCellPopovers';

export interface ProductListItem extends ProductRowChannelFields {
  id: string;
  name: string;
  image_url: string;
  category: string;
  is_active: boolean;
  show_in_store?: boolean;
  stock?: number | null;
  /** Precio de venta. */
  price: number;
  /** Precio de comparación (precio tachado). null si no está en oferta. */
  compare_at_price: number | null;
  /** Texto calculado para columna Inventario (ej. "5 en inventario" o "—") */
  inventoryDisplay: string;
  /** Texto calculado para columna Canales (ej. "2 canales (Tienda, POS)") */
  channelsDisplay: string;
}

export type NavigateFn = (path: string) => void;

export function getProductsListConfig(navigate: NavigateFn): ListViewConfig<ProductListItem> {
  return {
    title: 'Productos',
    titleIcon: ProductIcon,
    onBack: () => navigate(ROUTES.HOME),
    backAccessibilityLabel: 'Volver al inicio',
    primaryAction: {
      onAction: () => navigate(ROUTES.PRODUCTS_NEW),
      icon: PlusIcon,
    },
    resourceName: {
      singular: 'producto',
      plural: 'productos',
    },
    columns: [
      {
        key: 'image_url',
        title: 'Producto',
        type: 'thumbnailWithText',
        imageKey: 'image_url',
        textKey: 'name',
        altKey: 'name',
        fontWeight: 'medium',
      },
      {
        key: 'price',
        title: 'Precio',
        type: 'price',
      },
      {
        key: 'compare_at_price',
        title: 'Precio de comparación',
        type: 'price',
        // Precio "de antes" (el que el cliente ve tachado en la tienda). Acá es una columna aparte,
        // secundaria: tono suave, sin tachar. '—' si el producto no está en oferta.
        tone: 'subdued',
        fontWeight: 'regular',
      },
      {
        key: 'is_active',
        title: 'Estado',
        type: 'badge',
        map: { true: 'Activo', false: 'Inactivo' },
        toneFromValue: getStatusBadgeTone,
      },
      {
        key: 'inventoryDisplay',
        title: 'Inventario',
        type: 'text',
        tone: 'subdued',
      },
      {
        key: 'category',
        title: 'Categoría',
        type: 'text',
        tone: 'subdued',
      },
      {
        key: 'channelsDisplay',
        title: 'Canales',
        type: 'text',
        tone: 'subdued',
        // La celda resume; el desplegable dice en cuáles está, en cuáles no, y qué falló.
        expandableContent: makeChannelsExpandable(),
      },
      {
        key: 'actions',
        title: 'Acciones',
        type: 'actions',
        editRoute: ROUTES.PRODUCTS_EDIT,
        editLabel: 'Editar',
        delete: false,
      },
    ],
    emptyState: {
      heading: 'No hay productos',
      description: 'Crea tu primer producto para comenzar a vender.',
      image: 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png',
      action: {
        content: 'Agregar producto',
        onAction: () => navigate(ROUTES.PRODUCTS_NEW),
      },
    },
    bulkActions: [
      { content: 'Exportar datos', actionKey: 'export' },
      // "Eliminar" lo inyecta la plantilla vía prop `bulkDelete` (modal + toast integrados).
      { content: 'Activar seleccionados', actionKey: 'activate' },
      { content: 'Desactivar seleccionados', actionKey: 'deactivate' },
    ],
    idKey: 'id',
  };
}
